import React, { Suspense, useEffect, useState } from 'react';
import { Route, Router, Switch } from 'react-router-dom';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import PrivateRoute from './components/PrivateRoute';
import AppContainer from './containers/AppContainer';
import Spinner from './components/Spinner';
import { useAuth0 } from './react-auth0-spa';
import history from './utils/history';

import Footer from './components/Footer';
import COOKIES, { createCookiesJar } from './utils/cookies';
import CookiesAcceptationModal from './components/Modal/CookiesAcceptationModal';
import { NotFoundPage, Auth0ErrorPage } from './pages/ErrorPages';
import ServicesStatusPage from './pages/ServicesStatusPage';
import routes, { PATH_NAMES } from './routes';
import { getUserMetaDataFromToken } from './store/ProfileStore';
import { StoreContext, store } from './store';
import MainSpinner from './components/MainSpinner';
import {
  checkIfUserAccessRootPath,
  saveUserSessionPath,
  checkUserSessionUrl,
  checkIfPathIsAuth0ErrorPage,
} from './utils/routes';
import useTheme from './theme/useTheme';
import { DARK, LIGHT } from './utils/constants';

const { REACT_APP_ESETUP_STORE_URL_IOS, REACT_APP_WISERHOME_STORE_URL_IOS } = process.env;

const App = () => {
  const cookies = createCookiesJar();
  const { COOKIE_NECESSARY } = COOKIES;
  const { REACT_APP_NONCE } = process.env;

  const { loading, isAuthenticated, loginWithRedirect, user } = useAuth0();
  const [cookieState, setCookieState] = useState({
    isOpen: !cookies.get(COOKIE_NECESSARY),
    moreChoice: false,
  });

  const { theme } = useTheme();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme === DARK ? DARK : LIGHT);
  }, [theme]);

  if (checkIfPathIsAuth0ErrorPage(window.location.pathname)) {
    return <Auth0ErrorPage />;
  }

  const userData = user ? getUserMetaDataFromToken(user) : {};
  if (loading) {
    return <Spinner pageLoading />;
  }

  const openCookiesModalFromFooter = () => {
    setCookieState({
      moreChoice: true,
      isOpen: true,
    });
  };

  const closeCookiesModal = () => {
    setCookieState({
      moreChoice: false,
      isOpen: false,
    });
  };

  if (!isAuthenticated) {
    if (!checkIfUserAccessRootPath(window.location.pathname)) {
      saveUserSessionPath(window.location.pathname);
    }

    const url = new URL(window.location);

    const connectionUrlParam = url.searchParams.get('connection');
    if (connectionUrlParam) {
      loginWithRedirect({
        connection: connectionUrlParam,
      });
    } else {
      loginWithRedirect();
    }
  } else {
    const path = checkUserSessionUrl();
    if (path) history.push(path);
  }

  const cache = createCache({
    key: 'react-select',
    nonce: REACT_APP_NONCE,
    prepend: true,
  });

  return (
    <CacheProvider value={cache}>
      <Suspense fallback={<MainSpinner />}>
        <StoreContext.Provider value={store}>
          {isAuthenticated && (
            <CookiesAcceptationModal
              cookieState={cookieState}
              onCloseModal={closeCookiesModal}
              onMorePreferences={openCookiesModalFromFooter}
              cookies={cookies}
            />
          )}
          <Router history={history}>
            <Switch>
              <Route exact path={Object.values(PATH_NAMES)}>
                {isAuthenticated && (
                  <AppContainer id="outer-container">
                    {routes.map((route, index) => (
                      <PrivateRoute
                        key={index}
                        path={route.path}
                        exact={route.exact}
                        component={route.component}
                        available={route.available}
                        userType={userData.user_type}
                      />
                    ))}
                    <Footer openModal={openCookiesModalFromFooter} />
                  </AppContainer>
                )}
              </Route>
              {/* This is used for Universal links, it might be removed at later stages */}
              <Route
                exact
                path="/app/ch.feller.enduser"
                component={() => {
                  window.location.href = REACT_APP_WISERHOME_STORE_URL_IOS;
                  return null;
                }}
              />
              <Route
                exact
                path="/app/ch.feller.installer"
                component={() => {
                  window.location.href = REACT_APP_ESETUP_STORE_URL_IOS;
                  return null;
                }}
              />
              {isAuthenticated && <Route path="/status" component={ServicesStatusPage} />}
              {isAuthenticated && <Route path="*" component={NotFoundPage} />}
            </Switch>
          </Router>
        </StoreContext.Provider>
      </Suspense>
    </CacheProvider>
  );
};

export default App;
